import { useEffect, useMemo, useState } from "react"
import { toast } from "sonner"
import Pagination from "@/components/pagination"
import CreateAptForm from "./_components/create-apt-form"
import { adminAptStore } from "./admin-apt-store"
import { APT_COLUMNS, DATE_FIELDS, rowGridClass } from "./admin-apt-const"
import { deleteAptAPI, updateAptAPI } from "@/apis/admin-api"
import ActionToolbar from "@/routes/admin/_components/action-toolbar"
import SearchBar from "@/routes/admin/_components/search-bar"
import TableHeader from "../_components/table/table-header"
import TableData from "../_components/table/table-data"

const AdminAptPage = () => {
  const { list, pagination, updateQuery, apiAction } = adminAptStore()

  useEffect(() => {
    adminAptStore.getState().apiAction()
  }, [])

  const [selectedApt, setSelectedApt] = useState({
    kapt_code: "",
    apt_name: ""
  })

  const [keyword, setKeyword] = useState("")
  const [changedMap, setChangedMap] = useState({})

  const [isFormOpen, setIsFormOpen] = useState(false)

  const displayList = useMemo(() => {
    return list.map((item) => ({
      ...item,
      ...changedMap[item.kapt_code]
    }))
  }, [list, changedMap])

  const hasPendingChanges = useMemo(() => {
    return Object.keys(changedMap).length > 0
  }, [changedMap])

  const formatDateInput = (value) => {
    const m = value.match(/^(\d{4})[-. ]?(\d{2})[-. ]?(\d{2})$/)

    if (!m) return value

    return `${m[1]}-${m[2]}-${m[3]}`
  }

  const changeField = (kaptCode, key, value) => {
    const nextValue = DATE_FIELDS.includes(key) ? formatDateInput(value) : value

    const origin = list.find((v) => v.kapt_code === kaptCode)

    if (!origin) return

    const originValue = origin[key] ?? ""
    const changedValue = nextValue ?? ""

    setChangedMap((prev) => {
      const next = { ...prev }

      if (originValue === changedValue) {
        if (next[kaptCode]) {
          delete next[kaptCode][key]

          if (Object.keys(next[kaptCode]).length === 0) {
            delete next[kaptCode]
          }
        }

        return next
      }

      next[kaptCode] = {
        ...next[kaptCode],
        [key]: changedValue
      }

      return next
    })
  }

  const validateDateField = (kaptCode, key) => {
    const isDate = DATE_FIELDS.includes(key)

    if (!isDate) return
    const value = changedMap[kaptCode]?.[key]

    if (!value) return

    if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) {
      setChangedMap((prev) => ({
        ...prev,
        [kaptCode]: {
          ...prev[kaptCode],
          [key]: ""
        }
      }))
    }
  }

  const toggleCreateForm = () => {
    setIsFormOpen((prev) => !prev)
  }

  const rowClick = (item) => {
    setSelectedApt({
      kapt_code: item.kapt_code,
      apt_name: item.apt_name
    })
  }

  const deleteApt = async () => {
    if (!selectedApt.kapt_code) {
      return toast.error("선택된 아파트가 없습니다.")
    }

    const msg = `${selectedApt.apt_name}(${selectedApt.kapt_code})을(를) 삭제하시겠습니까?`
    if (!window.confirm(msg)) return

    const { success } = await deleteAptAPI({
      kapt_code: selectedApt.kapt_code
    })

    if (success) {
      toast.success(`${selectedApt.apt_name}가 삭제되었습니다.`)
      await apiAction()
    }
  }

  const updateApt = async () => {
    const payload = Object.entries(changedMap).map(([kapt_code, values]) => {
      const payloadItem = {}

      for (const key in values) {
        // ! 불리언 값이기 때문에 그대로 반환
        const value = values[key]

        payloadItem[key] = DATE_FIELDS.includes(key) && !value ? null : value
      }

      return {
        kapt_code,
        ...payloadItem
      }
    })

    const { success } = await updateAptAPI(payload)

    if (success) {
      toast.success("수정되었습니다.")
      await apiAction()
      setChangedMap({})
    }
  }

  const onSearch = async () => {
    await updateQuery({ keyword })
  }

  return (
    <section className="flex-1 flex flex-col">
      <article className="flex items-center justify-between">
        <SearchBar
          placeholder={"K-APT 코드, 아파트명으로 검색"}
          onChange={(e) => setKeyword(e.target.value)}
          value={keyword}
          onSubmit={onSearch}
        />

        <ActionToolbar
          onCreate={toggleCreateForm}
          isFormOpen={isFormOpen}
          onUpdate={updateApt}
          hasChanged={hasPendingChanges}
          onDelete={deleteApt}
        />
      </article>

      {isFormOpen && <CreateAptForm successAction={() => setIsFormOpen(false)} />}

      <TableHeader className={rowGridClass} columns={APT_COLUMNS} />

      <TableData
        datalist={displayList}
        columns={APT_COLUMNS}
        rowKey="kapt_code"
        className={rowGridClass}
        selected={selectedApt}
        rowClick={rowClick}
        onChange={changeField}
        onBlur={validateDateField}
      />

      <footer className="mt-auto">
        <Pagination
          currentPage={pagination.page}
          totalPages={Math.ceil(pagination.total / pagination.size)}
          onChange={(p) => updateQuery({ page: p })}
          activeStyle="bg-toicos-navy"
        />
      </footer>
    </section>
  )
}

export default AdminAptPage
